import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getPostsByLang } from "../data/posts";
import { useLocale } from "../context/LocaleContext";
import "../styles/root.css";
import "../styles/home.css";

function Home() {
  const { lang, t } = useLocale();
  const posts = getPostsByLang(lang);
  const sorted = [...posts].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  const heroPost = sorted[0];
  const latestPosts = sorted.slice(1, 4);

  const impactSlugs = [
    "humanitarian-happening-2022",
    "library-window-to-the-world",
    "consultation-on-senior-services",
  ];

  const impactPosts = impactSlugs
    .map((slug) => posts.find((post) => post.slug === slug))
    .filter(Boolean);

  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [lang]);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div>
      <Navbar />

      {/* ─── HERO ─── */}
      {heroPost && (
        <section className="hero">
          <div className="hero-image-wrap">
            <img
              className="hero-image"
              src={heroPost.mainImage.src}
              alt={heroPost.mainImage.alt}
            />
            <div className="hero-overlay" />
          </div>
          <div className="hero-content">
            <span className="hero-eyebrow">{t.home.heroTagline}</span>
            <h1 className="hero-title">{heroPost.title}</h1>
            <p className="hero-excerpt">{heroPost.excerpt}</p>
            <Link to={`/news/${heroPost.slug}`} className="hero-cta">
              {t.home.readMore}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </section>
      )}

      {/* ─── STATS RIBBON ─── */}
      <section className="stats-ribbon" ref={addRevealRef}>
        <div className="stats-ribbon-inner reveal-up">
          <div className="stat-item">
            <span className="stat-number">15+</span>
            <span className="stat-label">{t.home.statProjects}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">10+</span>
            <span className="stat-label">{t.home.statYears}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">100+</span>
            <span className="stat-label">{t.home.statCommunity}</span>
          </div>
        </div>
      </section>

      {/* ─── LATEST NEWS ─── */}
      <section className="section-latest">
        <div className="section-container">
          <div className="section-header" ref={addRevealRef}>
            <div className="reveal-up">
              <h2 className="section-title">{t.home.latestNews}</h2>
              <div className="section-title-line" />
            </div>
            <Link to="/news" className="section-see-all reveal-up">
              {t.home.viewAllNews}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
          <div className="latest-grid">
            {latestPosts.map((post, i) => (
              <article
                className="latest-card"
                key={post.slug}
                ref={addRevealRef}
                style={{ "--delay": `${i * 120}ms` }}
              >
                <Link to={`/news/${post.slug}`} className="latest-card-link reveal-up">
                  <div className="latest-card-image-wrap">
                    <img
                      className="latest-card-image"
                      src={post.mainImage.src}
                      alt={post.mainImage.alt}
                    />
                  </div>
                  <div className="latest-card-body">
                    <time className="latest-card-date">{post.date}</time>
                    <h3 className="latest-card-title">{post.title}</h3>
                    <p className="latest-card-excerpt">{post.excerpt}</p>
                    <span className="latest-card-read">
                      {t.home.readMore}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MISSION BAND ─── */}
      <section className="mission-band" ref={addRevealRef}>
        <div className="mission-band-inner reveal-up">
          <span className="mission-label">{t.home.missionLabel}</span>
          <p className="mission-text">{t.home.missionText}</p>
          <Link to="/about" className="mission-cta">
            {t.home.learnMore}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ─── IMPACT ─── */}
      <section className="section-impact">
        <div className="section-container">
          <div className="section-header" ref={addRevealRef}>
            <div className="reveal-up">
              <h2 className="section-title">{t.home.impactHeading}</h2>
              <div className="section-title-line" />
            </div>
          </div>
          <div className="impact-grid">
            {impactPosts.map((post, i) => (
              <article
                className="impact-card"
                key={post.slug}
                ref={addRevealRef}
                style={{ "--delay": `${i * 140}ms` }}
              >
                <Link to={`/news/${post.slug}`} className="impact-card-link reveal-up">
                  <div className="impact-card-image-wrap">
                    <img
                      className="impact-card-image"
                      src={post.mainImage.src}
                      alt={post.mainImage.alt}
                    />
                    <div className="impact-card-overlay" />
                  </div>
                  <div className="impact-card-body">
                    <h3 className="impact-card-title">{post.title}</h3>
                    <p className="impact-card-excerpt">{post.excerpt}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
